
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, Settings, Smartphone, Mail, Globe, Shield } from 'lucide-react';
import useAuth from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { DashboardCard } from './widgets';

const NotificationsTab = () => {
  const { user, profile } = useAuth();
  
  if (!user || !profile || !profile.notificationPreferences) return null;
  
  const { notificationPreferences } = profile;
  
  const handleToggleNotification = (type: keyof typeof notificationPreferences) => {
    // In a real app, this would update the database
    toast({
      title: "Preferences updated",
      description: "Your notification preferences have been saved."
    });
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-500/10 rounded-lg">
            <Bell className="h-6 w-6 text-blue-500" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Notifications</h1>
            <p className="text-muted-foreground">
              Customize how you receive updates about sunny terraces
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="bg-green-500/10 text-green-600 border-green-200">
            3 active
          </Badge>
          <Button variant="outline" size="sm">
            <Settings className="h-4 w-4 mr-1" />
            Advanced
          </Button>
        </div>
      </div>
      
      {/* Notification Preferences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DashboardCard
          title="Sun Notifications"
          description="Get alerts when your favorites are sunny"
          icon={<Bell className="h-5 w-5" />}
          variant="highlight"
          isPlaceholder
          placeholderText="Push Notifications Coming"
        >
          <div className="space-y-4">
            <div className="flex flex-row items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="favorite-sunny" className="font-medium">Favorite terraces sunny</Label>
                <p className="text-sm text-muted-foreground">
                  Instant alerts when sun hits your spots
                </p>
              </div>
              <Switch
                id="favorite-sunny"
                checked={notificationPreferences.favoriteTerracesSunny}
                onCheckedChange={() => handleToggleNotification('favoriteTerracesSunny')}
              />
            </div>
            
            <div className="flex flex-row items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="weather-updates" className="font-medium">Weather changes</Label>
                <p className="text-sm text-muted-foreground">
                  Get updates on weather changes
                </p>
              </div>
              <Switch
                id="weather-updates"
                checked={false}
                disabled
              />
            </div>
          </div>
        </DashboardCard>

        <DashboardCard
          title="Digest & Updates"
          description="Regular summaries and special offers"
          icon={<Mail className="h-5 w-5" />}
          variant="default"
        >
          <div className="space-y-4">
            <div className="flex flex-row items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="weekly-digest" className="font-medium">Weekly digest</Label>
                <p className="text-sm text-muted-foreground">
                  Summary of your sunny week
                </p>
              </div>
              <Switch
                id="weekly-digest"
                checked={notificationPreferences.weeklyDigest}
                onCheckedChange={() => handleToggleNotification('weeklyDigest')}
              />
            </div>
            
            <div className="flex flex-row items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="special-offers" className="font-medium">Special offers</Label>
                <p className="text-sm text-muted-foreground">
                  Partner deals and events
                </p>
              </div>
              <Switch
                id="special-offers"
                checked={notificationPreferences.specialOffers}
                onCheckedChange={() => handleToggleNotification('specialOffers')}
              />
            </div>
          </div>
        </DashboardCard>
      </div>

      {/* Notification Channels */}
      <DashboardCard
        title="Notification Channels"
        description="Choose how you want to receive notifications"
        icon={<Smartphone className="h-5 w-5" />}
        isPlaceholder
        placeholderText="Multi-Channel Support Coming"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg bg-gradient-to-br from-blue-50/30 to-white">
            <div className="flex items-center space-x-3 mb-2">
              <Smartphone className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Push Notifications</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Real-time alerts on your device
            </p>
            <Switch checked={true} disabled />
          </div>
          
          <div className="p-4 border border-border rounded-lg bg-gradient-to-br from-green-50/30 to-white">
            <div className="flex items-center space-x-3 mb-2">
              <Mail className="h-5 w-5 text-green-500" />
              <span className="font-medium">Email</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Weekly digests and updates
            </p>
            <Switch checked={true} />
          </div>
          
          <div className="p-4 border border-border rounded-lg bg-gradient-to-br from-purple-50/30 to-white">
            <div className="flex items-center space-x-3 mb-2">
              <Globe className="h-5 w-5 text-purple-500" />
              <span className="font-medium">Browser</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              In-app notifications
            </p>
            <Switch checked={false} disabled />
          </div>
        </div>
      </DashboardCard>
      
      {/* Privacy & Permissions */}
      <DashboardCard
        title="Privacy & Permissions"
        description="Manage your notification privacy settings"
        icon={<Shield className="h-5 w-5" />}
        variant="warning"
      >
        <div className="space-y-4">
          <div className="p-4 bg-blue-50/50 border border-blue-200/30 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-700 mb-1">Browser Permissions</h4>
                <p className="text-sm text-blue-600 mb-3">
                  Enable browser notifications for real-time sun alerts. We respect your privacy and only send relevant updates.
                </p>
                <Button size="sm" className="bg-blue-500 hover:bg-blue-600">
                  Enable Notifications
                </Button>
              </div>
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">
              <strong>Data Usage:</strong> We only use your notification preferences to send relevant updates about sunny terraces.
            </p>
            <p>
              <strong>Unsubscribe:</strong> You can turn off any notification type at any time from this page.
            </p>
          </div>
        </div>
      </DashboardCard>
    </div>
  );
};

export default NotificationsTab;
